import {
  Box,
  Container,
  Divider,
  HStack,
  Icon,
  Image,
  Link,
  SimpleGrid,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { FiMail, FiMapPin, FiPhone } from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";
import { NEWS_CATEGORIES } from "../../data/news";
import { SERVICES_DATA } from "../../data/services";
import { getNewsCategoryPath, getServiceDetailPath } from "../../config/routes";
import { HeroTexture } from "../common/HeroTexture";
import { CONTACT_INFO } from "../../data/contact";

const SOCIAL_IMAGE_MAP: Record<
  (typeof CONTACT_INFO.socials)[number]["key"],
  string
> = {
  telegram: "/images/telegram.png",
  instagram: "/images/instagram.png",
  ble: "/images/bale.png",
  eitaa: "/images/eitaa.png",
  linkedin: "/images/linkedin.png",
};

export const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const bgColor = useColorModeValue("dark.50", "dark.900");
  const textColor = useColorModeValue("gray.300", "gray.400");
  const headingColor = useColorModeValue("white", "white");
  const linkHoverColor = useColorModeValue("brand.400", "brand.300");
  const borderColor = useColorModeValue("gray.700", "gray.800");
  const socialBg = useColorModeValue("whiteAlpha.80", "whiteAlpha.80");
  const socialBorderColor = useColorModeValue(
    "whiteAlpha.200",
    "whiteAlpha.200",
  );

  const handleAnchorClick = (href: string) => {
    if (location.pathname === "/") {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      return;
    }

    navigate("/", { state: { scrollTo: href } });
  };

  const quickLinks = [
    { label: "خانه", onClick: () => handleAnchorClick("#hero") },
    { label: "خدمات تامین مالی", onClick: () => navigate("/services") },
    { label: "صندوق‌ها و محصولات", onClick: () => navigate("/funds") },
    { label: "اطلاع‌رسانی", onClick: () => navigate("/news") },
    { label: "امید کراد", href: "https://omidcf.ir/" },
    { label: "آکادمی مالی امید", href: "https://www.omidfa.ir/" },
    { label: "باشگاه مشتریان امید", onClick: () => navigate("/customer-club") },
    { label: "درباره ما", onClick: () => navigate("/about") },
    { label: "تماس با ما", onClick: () => navigate("/contact") },
  ];

  const serviceLinks = SERVICES_DATA.slice(0, 4).map((service) => ({
    label: service.shortTitle,
    onClick: () => navigate(getServiceDetailPath(service.id)),
  }));

  const newsLinks = NEWS_CATEGORIES.map((category) => ({
    label: category.title,
    onClick: () => navigate(getNewsCategoryPath(category.id)),
  }));

  return (
    <Box
      bg={bgColor}
      color={textColor}
      pt={16}
      pb={8}
      position="relative"
      overflow="hidden"
    >
      <HeroTexture
        patternConfig={{
          light: { layerOpacity: 0.18 },
          dark: { layerOpacity: 0.12 },
        }}
        glowConfig={{
          light: { opacity: 0.08, color: "rgba(173, 11, 6, 0.04)" },
          dark: { opacity: 0.08, color: "rgba(249, 168, 166, 0.04)" },
        }}
      />
      <Container maxW="container.xl" position="relative" zIndex={2}>
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={8} mb={10}>
          <VStack align="start" spacing={3}>
            <Text
              fontSize="lg"
              fontWeight="semibold"
              color={headingColor}
              mb={2}
            >
              دسترسی سریع
            </Text>
            {quickLinks.map((link) => (
              <Link
                key={link.label}
                href={link.href}
                isExternal={Boolean(link.href)}
                fontSize="sm"
                _hover={{ color: linkHoverColor }}
                transition="all 0.3s"
                cursor="pointer"
                onClick={link.onClick}
              >
                {link.label}
              </Link>
            ))}
          </VStack>

          <VStack align="start" spacing={3}>
            <Text
              fontSize="lg"
              fontWeight="semibold"
              color={headingColor}
              mb={2}
            >
              خدمات و محتوا
            </Text>
            {serviceLinks.map((link) => (
              <Link
                key={link.label}
                fontSize="sm"
                _hover={{ color: linkHoverColor }}
                transition="all 0.3s"
                cursor="pointer"
                onClick={link.onClick}
              >
                {link.label}
              </Link>
            ))}
            {newsLinks.map((link) => (
              <Link
                key={link.label}
                fontSize="sm"
                _hover={{ color: linkHoverColor }}
                transition="all 0.3s"
                cursor="pointer"
                onClick={link.onClick}
              >
                {link.label}
              </Link>
            ))}
          </VStack>

          <VStack align="start" spacing={3}>
            <Text
              fontSize="lg"
              fontWeight="semibold"
              color={headingColor}
              mb={2}
            >
              ارتباط با ما
            </Text>
            <HStack align="start" spacing={2}>
              <Icon
                verticalAlign="middle"
                as={FiPhone as any}
                boxSize={4}
                mt={1}
              />
              <Text fontSize="sm">۰۲۱-۸۸۵۱۵۶۹۵</Text>
            </HStack>
            <HStack align="start" spacing={2}>
              <Icon
                verticalAlign="middle"
                as={FiMail as any}
                boxSize={4}
                mt={1}
              />
              <Text fontSize="sm">info@omidib.com</Text>
            </HStack>
            <HStack align="start" spacing={2}>
              <Icon
                verticalAlign="middle"
                as={FiMapPin as any}
                boxSize={4}
                mt={1}
              />
              <Text fontSize="sm" lineHeight="tall">
                تهران، خیابان سهروردی شمالی، خیابان خرمشهر، کوچه شکوه، پلاک ۲
              </Text>
            </HStack>
            <HStack spacing={3} pt={2} flexWrap="wrap">
              {CONTACT_INFO.socials.map((social, index) => (
                <Link
                  key={`${social.key}-${index}`}
                  href={social.href}
                  isExternal
                  aria-label={social.label}
                  _hover={{
                    color: linkHoverColor,
                    textDecoration: "none",
                    transform: "translateY(-2px)",
                  }}
                  transition="all 0.3s"
                  borderRadius="full"
                >
                  <Box
                    boxSize={12}
                    display="inline-flex"
                    alignItems="center"
                    justifyContent="center"
                    borderRadius="full"
                    bg={socialBg}
                    border="1px solid"
                    borderColor={socialBorderColor}
                  >
                    <Image
                      src={SOCIAL_IMAGE_MAP[social.key]}
                      alt={social.label}
                      boxSize={7}
                      objectFit="contain"
                      display="block"
                      flexShrink={0}
                    />
                  </Box>
                </Link>
              ))}
            </HStack>
            <Box textAlign="center" mt={2}>
              <Link
                href="https://trustseal.enamad.ir/?id=468180&code=XZd6eAYwGKAFa6gxADp47ScXdiwa2dJh"
                rel="noopener noreferrer"
                display="inline-block"
                onClick={(event) => {
                  event.preventDefault();
                  window.open(
                    "https://trustseal.enamad.ir/?id=468180&code=XZd6eAYwGKAFa6gxADp47ScXdiwa2dJh",
                    "enamad",
                    "width=500,height=600,toolbar=no,scrollbars=no,resizable=no",
                  );
                }}
              >
                <Image
                  src="https://trustseal.enamad.ir/logo.aspx?id=468180&code=XZd6eAYwGKAFa6gxADp47ScXdiwa2dJh"
                  alt="نماد اعتماد الکترونیکی"
                  maxW="120px"
                  h="auto"
                  border="0"
                  mx="auto"
                  cursor="pointer"
                  referrerPolicy="origin"
                />
              </Link>
            </Box>
            {/* <Link
              fontSize="sm"
              _hover={{ color: linkHoverColor }}
              transition="all 0.3s"
              cursor="pointer"
              onClick={() => navigate("/contact")}
            >
              فرم تماس و نقشه دسترسی
            </Link> */}
          </VStack>
        </SimpleGrid>

        <Divider borderColor={borderColor} my={8} />

        {/* // ENAMAD */}
        <VStack spacing={3}>
          <Text fontSize="sm" textAlign="center">
            © ۱۴۰۵ تأمین سرمایه امید. تمامی حقوق محفوظ است.
          </Text>
          <Text fontSize="sm" textAlign="center" color={textColor}>
            این وب‌سایت برای معرفی خدمات، محصولات و محتوای اطلاع‌رسانی تامین
            سرمایه امید طراحی شده است.
          </Text>
        </VStack>
        {/* // ENAMAD */}
      </Container>
    </Box>
  );
};
